import { ASSET_STATUS, ASSET_TYPE, IAsset, assetIsDatalake, assetIsPublished, assetLinkProps, DivWithTooltip, getAssetImage, UserLink } from '@desp-aas/desp-ui-fwk';
import { icons, sourceMeta } from '../../utils/icons';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { BsDatabase, BsBraces, BsDisplay, BsFileText, BsMortarboard, BsHeart, BsDownload } from 'react-icons/bs';
import { Tooltip } from 'react-tooltip';
import removeMarkdown from 'remove-markdown';
import './AssetCardNewDetailed.css';

const TYPE_META: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
    [ASSET_TYPE.dataset]:     { icon: <BsDatabase />,  color: 'rgb(239,43,137)',  label: 'Dataset' },
    [ASSET_TYPE.model]:       { icon: <BsBraces />,    color: 'rgb(123,52,219)',  label: 'Model' },
    [ASSET_TYPE.application]: { icon: <BsDisplay />,    color: 'rgb(20,143,150)',  label: 'Application' },
    [ASSET_TYPE.paper]:       { icon: <BsFileText />,  color: 'rgb(247,164,56)',  label: 'Document' },
    [ASSET_TYPE.course]:      { icon: <BsMortarboard />, color: 'rgb(99,193,224)', label: 'Course' },
};

type IProps = {
    asset: IAsset
};

export function AssetCardNewDetailed(props: IProps) {
    const { asset } = props;
    const [imgError, setImgError] = useState(false);
    const meta = TYPE_META[asset.type] ?? TYPE_META[ASSET_TYPE.dataset];
    const desc = removeMarkdown(asset.metadata?.description ?? '').trim();
    const shortDesc = desc.length > 220 ? desc.slice(0, 220) + '…' : desc;
    const cssVars = { '--acn-color': meta.color } as React.CSSProperties;
    const source = sourceMeta(asset.source);

    const tooltipId = `acnd-src-${asset.id}`;

    return (
        <NavLink {...assetLinkProps(asset)} className="acnd" style={cssVars}>
            <div className="acnd-img" data-type={asset.type}>
                {!imgError
                    ? <img src={getAssetImage(asset)} alt={asset.name} className="acnd-real-img" onError={() => setImgError(true)} />
                    : <span className="acnd-img-watermark">{meta.icon}</span>
                }
                <div className="acnd-img-overlay" />
                <div className="acnd-badges">
                    {asset.isBookmarked &&
                        <DivWithTooltip tooltip="Bookmarked" className="acnd-badge acnd-badge--bookmark">
                            {icons.assets.isBookmarked}
                        </DivWithTooltip>
                    }
                    {asset.status === ASSET_STATUS.rejected.valueOf() &&
                        <DivWithTooltip tooltip="Rejected" className="acnd-badge acnd-badge--rejected">
                            {icons.asset_status.rejected}
                        </DivWithTooltip>
                    }
                    {!assetIsPublished(asset) &&
                        <DivWithTooltip tooltip="Draft" className="acnd-badge acnd-badge--draft">
                            {icons.assets.private}
                        </DivWithTooltip>
                    }
                </div>
            </div>
            <div className="acnd-body">
                <span
                    className={`acnd-source-badge acnd-source-badge--${source.modifier}`}
                    data-tooltip-id={tooltipId}
                    data-tooltip-content={source.label}
                >
                    {source.icon}
                </span>
                {createPortal(
                    <Tooltip id={tooltipId} place="left" positionStrategy="fixed" />,
                    document.body
                )}
                <div className="acnd-type-badge"><span className="acnd-type-icon">{meta.icon}</span>{meta.label}</div>
                <div className="acnd-name">{asset.name}</div>
                {desc && <div className="acnd-desc">{shortDesc}</div>}
                <div className="acnd-footer">
                    {/* datalake assets have no local likes/downloads to show */}
                    {!assetIsDatalake(asset) &&
                        <div className="acnd-stats">
                            <span className="acnd-stat"><BsHeart /> {asset.likes_count ?? 0}</span>
                            <span className="acnd-stat"><BsDownload /> {asset.downloads_count ?? 0}</span>
                        </div>
                    }
                    {asset.despUserId && <UserLink username={asset.despUserId} />}
                </div>
            </div>
        </NavLink>
    );
}

export default AssetCardNewDetailed;
